import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { Status } from '../../hooks/useFetchState'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { getQualityControlDetail, patchOrderDetailReturnReason } from './api'
import { usePagination } from '../../hooks/usePagination'
import { putReceiveASN } from '../../pages/Returns/config/api'
import { eAsnState } from '../../enums/eAsnState'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import { LoadingButton } from '../_EOS/Button/LoadingButton'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../_EOS/Modal'
import { GenericTableExpand } from '../_EOS/Table/GenericTableExpand'
import { TableHeaderConfigExpand } from '../_EOS/Table/types'
import { SaveIconButton } from '../Buttons/SaveIcon'
import DeclareError from '../DispatchOt/DeclareError'
import { OrderDetailReturnReasonPatchDto } from './types'
import InfoIcon from '@mui/icons-material/Info'
import { Loading } from '../UI'
import { StepReturnQualityFooterInfo } from './components/StepReturnQualityFooterInfo'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { getStyleRow, mapBatchStatusToExpiredColor } from '../../utils/colors'
import { formatExpirationDate } from '../../utils/dateHelper'

export interface AsnReturnQualityControlInfoDto {
  /** ID de la razón de devolución del detalle */
  orderDetailReturnReasonId: number
  /** ID del detalle de la orden */
  orderDetailId: number
  /** ID del detalle ASN */
  asnDetailId: number
  /** Estado del ASN */
  asnStateId: number
  /** URL de imagen del SKU */
  skuUrlImage?: string | null
  /** Código SKU del producto */
  sku: string
  /** Código EAN13 del producto */
  ean13: string
  /** Nombre del producto */
  productName: string
  /** Identificador del lote */
  batchId?: number | null
  /** Lote */
  batchNumber: string
  /** Fecha de vencimiento */
  expirationDate?: string | null
  /** Identificador del motivo de devolución */
  returnReasonId: number
  /** Motivo de devolución */
  returnReason: string
  /** Cantidad total devuelta */
  returnedQty: number
  /** Cantidad en perfecto estado */
  inPerfectStateQty?: number | null
  /** Cantidad mermada */
  shrinkageQty?: number | null
  /** Cantidad faltante */
  shortageQty?: number | null
  /** Cantidad rechazada comercialmente */
  commercialRejectionQty?: number | null
  /** Coincide el motivo de devolución */
  returnReasonMatches?: boolean | null
  /** Comentarios cuando el motivo no coincide */
  comment?: string | null
  /** indica si esta vencido por vencer o vigente (existe enums) */
  batchStatus?: number | null
}

export interface StepReturnQualityProps {
  handleStep: React.Dispatch<React.SetStateAction<number>>
  handleSnackbar: (msg: string | undefined, status: boolean) => void
  handleCloseModal: () => void
  asnId: number
  asnNumber: string
  selectedAsnStateId: number
  userId: number
}

type CustomActionsProps = {
  row: AsnReturnQualityControlInfoDto
}

const setImage = (pImage: AsnReturnQualityControlInfoDto[] | unknown) => {
  const imageAux = pImage as AsnReturnQualityControlInfoDto[]

  const image =
    imageAux && imageAux.length > 0
      ? imageAux[0].skuUrlImage
      : 'https://via.placeholder.com/150/e5e5e5/808080?text=Sin%20Imagen'

  return (
    <Fragment>
      <Avatar
        style={{ height: '100px', width: '100px' }}
        variant='square'
        alt='SkuImage'
        src={image || ''}
      />
    </Fragment>
  )
}

// muestra el sku y muestra icono por controlar
const setSkuAndIconToControl = (
  sku: string | unknown,
  fileItem: AsnReturnQualityControlInfoDto | undefined
) => {
  const displaySku = typeof sku === 'string' ? sku : `${sku ?? ''}`

  return (
    <Fragment>
      {displaySku}
      {fileItem && fileItem.asnStateId === eAsnState.Received && (
        <Box
          style={{
            fontSize: '4px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#c83153',
            color: 'white',
            padding: '2px',
            margin: '3px',
            borderRadius: 8,
            cursor: 'pointer',
            width: 124
          }}
        >
          <InfoIcon style={{ margin: '4px' }} fontSize='small' />
          <Typography variant='body2'>{'Por Controlar'}</Typography>
        </Box>
      )}
    </Fragment>
  )
}

export const StepReturnQuality: React.FC<StepReturnQualityProps> = ({
  handleSnackbar,
  asnId,
  selectedAsnStateId,
  userId
}) => {
  const declareErrorModal = useModal('')
  const [showGrid, setShowGrid] = useState<boolean>(false) // permite mostrar la información cuando la asn se encuentre en estado recepcionada.
  const [searchSku, setSearchSku] = useState('') // permite mostrar la información cuando la asn se encuentre en estado recepcionada.
  const { state: stateAsnReturn, callApi } =
    useApi<ApiResponse<AsnReturnQualityControlInfoDto>>()
  const { callApi: ReceiveASNcallApi } = useApi()
  const { callApi: patchOrderDetailReturnReasonCallApi } = useApi()
  const pagination = usePagination(5)
  const [loading, setLoading] = useState(false) // loading para formulario de pregunta en recepcionar Asn antes de motrar formulario calidad
  const [loadingButton, setLoadingButton] = useState(false)
  // Estado para guardar los cambios de todas las filas
  const [rowChanges, setRowChanges] = useState<
    Record<string, Partial<AsnReturnQualityControlInfoDto>>
  >({})
  // tableKey: Es solo para cuando se presione el boton limpiar (fila), pueda renderizar nuevamente la tabla sin tener q llamar api nuevamente, ya que se limpio solo la fila
  // si se elimina la fila solamente en rowChanges siguen estando los datos visibles debido a que no renderiza nuevamente la grilla. con tableKey se soluciona. (se probo con muchas formas y esta fue la mejor)
  const [tableKey, setTableKey] = useState(0)

  // Función para manejar cambios en los campos editables
  const handleFieldChange = (
    rowId: string | number,
    field: keyof AsnReturnQualityControlInfoDto,
    newValue: any
  ) => {
    setRowChanges((prev) => {
      const updated = {
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [field]: newValue
        }
      }
      return updated
    })
  }

  // Método para renderizar la columna de coincidencia de motivo de devolución
  const renderReturnReasonMatchesColumn = (
    value: boolean | null,
    row: AsnReturnQualityControlInfoDto | undefined
  ) => {
    if (!row) return <Fragment></Fragment>

    const finalRowId = row.orderDetailReturnReasonId
    const changes = rowChanges[finalRowId] || {}

    // Usar el valor de rowChanges si existe, sino usar el valor original
    const currentValue =
      changes.returnReasonMatches !== undefined
        ? changes.returnReasonMatches
        : value

    // Si ya tiene un valor definido (true), mostrar "Sí"
    if (currentValue === true) {
      return <Typography variant='body2'>Sí</Typography>
    }

    // Si es false, mostrar "No" y campo de texto para comentario
    if (currentValue === false) {
      const currentComment =
        changes.comment !== undefined ? changes.comment : row.comment
      const commentLength = currentComment ? currentComment.length : 0
      const maxLength = 100
      const minLength = 5
      const hasError = commentLength > 0 && commentLength < minLength

      return (
        <Box style={{ minWidth: '250px' }}>
          <TextField
            value={currentComment || ''}
            onChange={(e) => {
              // Solo permitir cambios si es Received
              const newValue = e.target.value
              if (newValue.length <= maxLength) {
                handleFieldChange(finalRowId, 'comment', newValue)
              }
            }}
            placeholder='Ingrese comentario (mín. 5 caracteres)'
            variant='outlined'
            size='small'
            fullWidth
            multiline
            rows={2}
            error={hasError}
            disabled={row.comment !== null}
            helperText={
              hasError
                ? `Mínimo ${minLength} caracteres`
                : `${commentLength}/${maxLength} (No coincide)`
            }
            inputProps={{
              maxLength: maxLength
            }}
          />
        </Box>
      )
    }

    // Si es null, mostrar los botones "Si" y "No"
    return (
      <Box style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={() => {
            // Solo permitir cambios solo si tiene el campo sin valor
            handleFieldChange(finalRowId, 'returnReasonMatches', true)
          }}
          disabled={row.returnReasonMatches !== null}
        >
          Sí
        </Button>
        <Button
          variant='contained'
          color='secondary'
          size='small'
          onClick={() => {
            // Solo permitir cambios solo si tiene el campo sin valor
            handleFieldChange(finalRowId, 'returnReasonMatches', false)
          }}
          disabled={row.returnReasonMatches !== null}
        >
          No
        </Button>
      </Box>
    )
  }

  const tableHeader: TableHeaderConfigExpand<AsnReturnQualityControlInfoDto>[] =
    [
      {
        field: 'skuUrlImage',
        title: 'Imagen',
        sortable: false,
        formater: (image: unknown) => setImage(image as unknown)
      },
      {
        field: 'sku',
        title: 'SKU/EAN13',
        sortable: false,
        formater: (value, row) =>
          setSkuAndIconToControl(value, row || undefined)
      },
      {
        field: 'batchNumber',
        title: 'Lote/Fecha Vencimiento',
        sortable: false,
        formater: (value, row) => {
          if (!row) return ''

          // Si value tiene contenido válido, mostrarlo
          if (value !== null && value !== undefined && value !== '') {
            return value.toString()
          }

          // Si value está vacío/null/undefined, intentar con expirationDate
          const expiration = formatExpirationDate(
            row?.expirationDate,
            'dd-MM-yyyy'
          )

          return expiration || 'N/A'
        }
      },
      {
        field: 'returnReason',
        title: 'Motivo Devolución',
        sortable: false
      },
      {
        field: 'returnedQty',
        title: 'Cantidad devuelta',
        sortable: false
      },
      {
        field: 'inPerfectStateQty',
        title: 'En Perfecto Estado',
        sortable: false,
        editable: (row) => {
          return row.returnReasonMatches == null
        },
        inputType: 'number',
        onEditChange: (rowId, newValue) => {
          handleFieldChange(rowId, 'inPerfectStateQty', newValue)
        }
      },
      {
        field: 'shrinkageQty',
        title: 'Enviar a calidad',
        sortable: false,
        editable: (row) => {
          return row.returnReasonMatches == null
        },
        inputType: 'number',
        onEditChange: (rowId, newValue) => {
          handleFieldChange(rowId, 'shrinkageQty', newValue)
        },
        formater: (value, row) => {
          if (!row)
            return <Typography variant='body2'>{value as number}</Typography>

          const changes = rowChanges[row.orderDetailReturnReasonId]
          const displayValue =
            changes?.shrinkageQty !== undefined ? changes.shrinkageQty : value

          return (
            <Typography variant='body2'>{displayValue as number}</Typography>
          )
        }
      },
      {
        field: 'shortageQty',
        title: 'Declarar faltante',
        sortable: false,
        editable: (row) => {
          return row.returnReasonMatches == null
        },
        inputType: 'number', // NUEVO
        onEditChange: (rowId, newValue) => {
          handleFieldChange(rowId, 'shortageQty', newValue)
        }
      },
      {
        field: 'commercialRejectionQty',
        title: 'Rechazo comercial',
        sortable: false,
        editable: (row) => {
          return row.returnReasonMatches == null
        },
        inputType: 'number', // NUEVO
        onEditChange: (rowId, newValue) => {
          handleFieldChange(rowId, 'commercialRejectionQty', newValue)
        }
      },
      {
        field: 'returnReasonMatches',
        title: 'Coincide Motivo Devolución',
        sortable: false,
        formater: (value, row) =>
          renderReturnReasonMatchesColumn(value as boolean | null, row)
      }
    ]

  const requestAsnReturn = (
    sku: string | undefined,
    page?: number,
    pageSize?: number
  ) => {
    setLoadingButton(true)
    callApi(getQualityControlDetail(sku, asnId, page, pageSize))
      .then(() => setLoadingButton(false))
      .catch((error) => {
        setLoadingButton(false)
        console.log(error)
      })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestAsnReturn(undefined, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleReceiveAsn = () => {
    setLoading(true)
    ReceiveASNcallApi(putReceiveASN(asnId, userId)).then(() => {
      requestAsnReturn(undefined, 0, pagination.pageSize)
      setShowGrid(true) // activa la grilla depues de recepcionarla
      setLoading(false)
    })
  }

  // Componente de acciones personalizado actualizado
  // Componente de acciones personalizado actualizado
  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    const handleSave = () => {
      setLoadingButton(true)
      const finalRowId = row.orderDetailReturnReasonId
      const changes = rowChanges[finalRowId] || {}

      // Validar que returnReasonMatches tenga un valor seleccionado
      const finalReturnReasonMatches =
        'returnReasonMatches' in changes
          ? changes.returnReasonMatches
          : row.returnReasonMatches

      if (
        finalReturnReasonMatches === null ||
        finalReturnReasonMatches === undefined
      ) {
        handleSnackbar(
          'Debe seleccionar si coincide o no el motivo de devolución',
          false
        )
        setLoadingButton(false)
        return
      }

      // Validar comentario si returnReasonMatches es false
      if (finalReturnReasonMatches === false) {
        const finalComment =
          'comment' in changes ? changes.comment : row.comment
        if (!finalComment || finalComment.trim().length < 5) {
          handleSnackbar(
            'Debe ingresar un comentario de al menos 5 caracteres',
            false
          )
          setLoadingButton(false)
          return
        }
      }

      const getValue = (changedValue: any, originalValue: any) => {
        if (changedValue !== undefined && changedValue !== null) {
          return changedValue
        }
        if (originalValue !== undefined && originalValue !== null) {
          return originalValue
        }
        return 0
      }

      const totalQty =
        getValue(changes.inPerfectStateQty, row.inPerfectStateQty) +
        getValue(changes.shrinkageQty, row.shrinkageQty) +
        getValue(changes.shortageQty, row.shortageQty) +
        getValue(changes.commercialRejectionQty, row.commercialRejectionQty)

      if (totalQty > row.returnedQty) {
        handleSnackbar(
          'La suma de cantidades no puede exceder la cantidad devuelta.',
          false
        )

        setLoadingButton(false)
        return
      }

      if (totalQty === 0) {
        handleSnackbar(
          'Es necesario proporcionar las cantidades para el control de calidad.',
          false
        )

        setLoadingButton(false)
        return
      }

      const dto: OrderDetailReturnReasonPatchDto = {
        inPerfectStateQty:
          'inPerfectStateQty' in changes
            ? changes.inPerfectStateQty!
            : row.inPerfectStateQty,
        shrinkageQty:
          'shrinkageQty' in changes ? changes.shrinkageQty! : row.shrinkageQty,
        shortageQty:
          'shortageQty' in changes ? changes.shortageQty! : row.shortageQty,
        commercialRejectionQty:
          'commercialRejectionQty' in changes
            ? changes.commercialRejectionQty!
            : row.commercialRejectionQty,
        returnReasonMatches:
          'returnReasonMatches' in changes
            ? changes.returnReasonMatches!
            : row.returnReasonMatches,
        comment: 'comment' in changes ? changes.comment! : row.comment || null
      }

      patchOrderDetailReturnReasonCallApi(
        patchOrderDetailReturnReason(asnId, row.orderDetailReturnReasonId, dto)
      )
        .then(() => {
          setLoadingButton(false)
          handleSnackbar('Datos guardados correctamente', true)
          setRowChanges((prev) => {
            const newChanges = { ...prev }
            delete newChanges[finalRowId]
            return newChanges
          })
          requestAsnReturn(undefined, pagination.page, pagination.pageSize)
        })
        .catch((e) => {
          setLoadingButton(false)
          handleSnackbar(`Error: ${e}`, false)
        })
    }

    const handleClearChanges = () => {
      setLoadingButton(true)
      const finalRowId = row.orderDetailReturnReasonId
      setRowChanges((prev) => {
        const newChanges = { ...prev }
        delete newChanges[finalRowId]
        return newChanges
      })

      // Cambiar la clave para forzar re-mount del componente
      setTableKey((prev) => prev + 1)

      setLoadingButton(false)
      handleSnackbar('Cambios descartados', true)
    }

    return (
      <Fragment>
        {loadingButton && <Loading />}
        {!loadingButton && (
          <Tooltip title='Guardar' aria-label='clear'>
            <SaveIconButton
              fontSize='medium'
              onClick={handleSave}
              isLoading={loading}
              disabled={row.returnReasonMatches !== null}
            />
          </Tooltip>
        )}
        {!loadingButton && (
          <Tooltip title='Descartar cambios' aria-label='clear'>
            <IconButton
              onClick={handleClearChanges}
              color='default'
              disabled={row.returnReasonMatches !== null}
            >
              <RotateLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Fragment>
    )
  }

  useEffect(() => {
    // si esta recepcionada debe mostrar grilla
    // o si controlaron solo un item deberia mostar también (Controlling)
    if (
      selectedAsnStateId === eAsnState.Received ||
      selectedAsnStateId === eAsnState.Controlling
    ) {
      setShowGrid(true)
      requestAsnReturn(undefined, 0, pagination.pageSize)
    }

    if (selectedAsnStateId === eAsnState.Pending) {
      handleReceiveAsn()
    }
  }, [])

  const handleSearchSkuKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      handleSearchSku()
    }
  }

  const handleSearchSku = () => {
    requestAsnReturn(searchSku, 0, pagination.pageSize)
  }

  // Eventos para snackbar de formulario declarar error
  const showErrorFinishingDeclareErrors = () =>
    handleSnackbar('Error al finalizar', false)

  const showErrorGettingBeforeDeclaredErrors = () =>
    handleSnackbar('Error obteniendo errores declarados previamente', false)

  const showSuccessFinishingDeclareErrors = () => {
    handleSnackbar(
      'Registro de producto no esperado finalizó correctamente',
      true
    )
    declareErrorModal.closeModal()
  }

  const showErrorSkuValidation = (message: string) =>
    handleSnackbar(message, false)

  const getRowStyle = (batchState: number) => {
    return getStyleRow(mapBatchStatusToExpiredColor(batchState))
  }

  return (
    <Fragment>
      <Grid container spacing={2}>
        {/* al presionar el boton si queda loading esperando respuesta de la recepcion de asn creada recientemente */}
        {loading && (
          <Grid item xs={12} md={12}>
            <Grid
              container
              direction='column'
              alignItems='center'
              justifyContent='center'
              style={{ minHeight: '100px' }}
            >
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <InfoOutlinedIcon
                      color='action'
                      fontSize='medium'
                      style={{ color: '#FF9800' }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant='body1'>
                      Recepcionando Asn...
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item style={{ marginTop: '16px' }}>
                <Loading />
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Busqueda  */}
        {showGrid && (
          <Grid item xs={12} md={12} style={{ paddingTop: '17px' }}>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12} sm={3} md={3}>
                <TextField
                  placeholder='Buscar SKU'
                  style={{ alignSelf: 'flex-start' }}
                  fullWidth
                  value={searchSku}
                  onChange={(e) => setSearchSku(e.target.value)}
                  onKeyPress={handleSearchSkuKeyPress}
                  variant='outlined'
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} sm={1} md={1}>
                <Tooltip title='Limpiar'>
                  <IconButton
                    color='primary'
                    onClick={() => {
                      setSearchSku('')
                      requestAsnReturn(undefined, 0, pagination.pageSize)
                    }}
                    disabled={!searchSku}
                  >
                    <RotateLeftIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <LoadingButton
                  loading={loadingButton}
                  variant='contained'
                  color='primary'
                  onClick={() => handleSearchSku()}
                  disabled={searchSku === '' || searchSku.length === 0}
                >
                  Buscar
                </LoadingButton>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  disabled={loadingButton}
                  type='button'
                  variant='contained'
                  color='primary'
                  onClick={() => declareErrorModal.openModal('sm', '')}
                  style={{ alignSelf: 'flex-start' }}
                >
                  REGISTRAR PRODUCTO NO ESPERADO
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* muestra la grilla solo si la Asn está recepcionada. */}
        {showGrid && (
          <Grid item xs={12} md={12} alignContent='center' alignItems='center'>
            <GenericTableExpand<AsnReturnQualityControlInfoDto>
              rowConfig={
                stateAsnReturn.data?.items.map((el) =>
                  getRowStyle(el?.batchStatus || 0)
                ) as any
              }
              key={`table-${tableKey}`}
              dense={true}
              headersConfig={tableHeader}
              IDKey={'orderDetailReturnReasonId'}
              data={stateAsnReturn.data?.items || []}
              pagination={{
                totalItem: stateAsnReturn.data?.totalItems || 0,
                page: pagination.page,
                pageSize: pagination.pageSize
              }}
              onPaginationChange={handlePaginationChange}
              isLoading={stateAsnReturn.status === Status.Loading}
              showCheckboxes={false}
              customActionComponent={MyCustomActions}
            />
          </Grid>
        )}

        {showGrid && (
          <Grid
            item
            xs={12}
            md={12}
            justifyContent='flex-end'
            alignContent='flex-end'
            alignItems='flex-end'
          >
            <StepReturnQualityFooterInfo />
          </Grid>
        )}
      </Grid>
      <Modal
        open={declareErrorModal.isOpen}
        onClose={declareErrorModal.closeModal}
        title={declareErrorModal.title}
        maxWidth={declareErrorModal.size}
      >
        <DeclareError
          withComments={true}
          userId={userId}
          asnId={asnId!}
          isSkuValidation={false}
          withInventoryIncident={false}
          closeDeclareError={declareErrorModal.closeModal}
          errorFinishingDeclareErrors={showErrorFinishingDeclareErrors}
          errorGettingBeforeDeclaredErrors={
            showErrorGettingBeforeDeclaredErrors
          }
          successFinishingDeclareErrors={showSuccessFinishingDeclareErrors}
          onErrorSkuValidation={showErrorSkuValidation}
        />
      </Modal>
    </Fragment>
  )
}
